import { MatDialog } from '@angular/material/dialog';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../models/response';
import { StationService } from './../../../services/station.service';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-station',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './station.component.html',
  styleUrl: './station.component.css',
})
export class StationComponent {
  stationService = inject(StationService);
  ngxSpinnerService = inject(NgxSpinnerService);
  stations: any = [];
  displayLoading: boolean = false;
  viewMode: 'grid' | 'list' = 'grid';
  /** List / Grid lives in the URL (?view). */
  private urlState = injectUrlViewState();
  searchTerm: string = '';

  get filteredStations(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.stations;
    }
    return this.stations.filter(
      (station: any) =>
        station.station_name?.toLowerCase().includes(term) ||
        this.typeLabel(station.station_type).toLowerCase().includes(term),
    );
  }

  onSearch(event: any) {
    this.searchTerm = event.target.value;
  }

  /** Up to two initials for the card avatar, e.g. "Main Kitchen" -> "MK". */
  initials(name: string): string {
    return (name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }

  typeLabel(type: string): string {
    if (type === 'food_runner') {
      return 'Food Runner';
    }
    if (type === 'counter') {
      return 'Counter';
    }
    if (type === 'bar') {
      return 'Bar';
    }
    return 'Kitchen';
  }

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.viewMode = this.urlState.pick<'grid' | 'list'>('view', ['grid', 'list'], 'grid');
    this.getStations();
  }

  /** List / Grid, kept in the URL (?view) so a browser reload keeps it. */
  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
    this.urlState.set('view', mode, 'grid');
  }

  getStations() {
    this.displayLoading = true;
    this.stationService.getStations().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.stations = response.data;
        this.stations.sort((a: any, b: any) => a.station_name.localeCompare(b.station_name));
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  addNewStation(station?: any) {
    this.router.navigate(station ? ['/edit-station', station.station_id] : ['/add-station']);
  }

  saveNewStation(stationData: any) {
    this.ngxSpinnerService.show();
    this.stationService.addStation(stationData).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('Station added successfully', 'Success');
          this.getStations();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  editStation(stationData: any, stationId: number) {
    this.ngxSpinnerService.show();
    this.stationService.editStation(stationData, stationId).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('Station updated successfully', 'Success');
          this.getStations();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  deleteStationConfirm(station: any) {
    if (!(station.is_default && station.station_type == 'kitchen')) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'This will permanently delete this selected station. If this station is linked with any devices, those devices will also be deleted and you can’t undo this later.',
          buttonText: {
            ok: 'Yes, Delete',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteStation(station);
        }
      });
    }
  }

  deleteStation(station: any) {
    this.ngxSpinnerService.show();
    this.stationService.deleteStation(station.station_id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Station deleted successfully', 'Success');
        this.ngxSpinnerService.hide();
        this.getStations();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }
}
